import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, Edit2, Trash2, X, Save, Eye, EyeOff,
  DollarSign, ShoppingBag, Tag, Search, Percent
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInfluencers = () => {
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    coupon_code: '',
    commission_type: 'percentage',
    commission_value: 10,
    is_active: true
  });

  useEffect(() => {
    fetchInfluencers();
  }, []);

  const fetchInfluencers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/influencers/admin/list`);
      const data = await res.json();
      setInfluencers(data.influencers || []);
    } catch (error) {
      toast.error('Error al cargar influencers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingId 
        ? `${API_URL}/api/influencers/admin/${editingId}`
        : `${API_URL}/api/influencers/admin/create`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      // Don't send empty password on edit
      const payload = { ...formData };
      if (editingId && !payload.password) {
        delete payload.password;
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(editingId ? 'Influencer actualizado' : 'Influencer creado');
        setShowModal(false);
        resetForm();
        fetchInfluencers();
      } else {
        toast.error(data.detail || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const handleEdit = (influencer) => {
    setFormData({
      name: influencer.name,
      email: influencer.email,
      password: '',
      coupon_code: influencer.coupon_code,
      commission_type: influencer.commission_type || 'percentage',
      commission_value: influencer.commission_value || influencer.commission_percentage || 10,
      is_active: influencer.is_active
    });
    setEditingId(influencer.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este influencer?')) return;

    try {
      const res = await fetch(`${API_URL}/api/influencers/admin/${id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        toast.success('Influencer eliminado');
        fetchInfluencers();
      } else {
        toast.error('Error al eliminar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      coupon_code: '',
      commission_type: 'percentage',
      commission_value: 10,
      is_active: true
    });
    setEditingId(null);
  };

  const filteredInfluencers = influencers.filter(inf =>
    inf.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inf.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inf.coupon_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="text-neon-cyan" />
            Influencers
          </h1>
          <p className="text-white/50 text-sm mt-1">
            Gestiona influencers y sus comisiones
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-neon-cyan text-neon-dark font-medium rounded-lg hover:bg-neon-cyan/90 transition-colors"
        >
          <Plus size={18} />
          Nuevo Influencer
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          type="text"
          placeholder="Buscar por nombre, email o código..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-neon-dark border border-neon-cyan/30 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-neon-cyan"
        />
      </div>

      {/* Influencers Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin" />
        </div>
      ) : filteredInfluencers.length === 0 ? (
        <div className="text-center py-12 bg-neon-dark border border-neon-cyan/20 rounded-xl">
          <Users size={48} className="mx-auto text-white/20 mb-4" />
          <p className="text-white/40">
            {searchQuery ? 'No se encontraron influencers' : 'No hay influencers registrados'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredInfluencers.map((influencer) => (
            <div
              key={influencer.id}
              className={`bg-neon-dark border rounded-xl p-5 ${
                influencer.is_active ? 'border-neon-cyan/20' : 'border-red-500/30 opacity-60'
              }`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Info */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-neon-cyan/20 to-neon-pink/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-white">
                      {influencer.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-white">{influencer.name}</h3>
                      {!influencer.is_active && (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                          Inactivo
                        </span>
                      )}
                    </div>
                    <p className="text-white/50 text-sm">{influencer.email}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <div className="flex items-center gap-1.5">
                        <Tag size={14} className="text-neon-cyan" />
                        <span className="text-neon-cyan font-mono font-bold">
                          {influencer.coupon_code}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {influencer.commission_type === 'fixed' ? (
                          <>
                            <DollarSign size={14} className="text-neon-pink" />
                            <span className="text-neon-pink font-bold">
                              ${influencer.commission_value} / perfume
                            </span>
                          </>
                        ) : (
                          <>
                            <Percent size={14} className="text-neon-pink" />
                            <span className="text-neon-pink font-bold">
                              {influencer.commission_value || influencer.commission_percentage}% comisión
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stats & Actions */}
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2 px-4 py-2 bg-neon-darker rounded-lg">
                    <ShoppingBag size={16} className="text-white/40" />
                    <span className="text-white font-bold">{influencer.order_count || 0}</span>
                    <span className="text-white/40 text-sm">pedidos</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(influencer)}
                      className="p-2 text-white/60 hover:text-neon-cyan hover:bg-neon-cyan/10 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(influencer.id)}
                      className="p-2 text-white/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/70 overflow-y-auto">
          <div className="bg-neon-dark border border-neon-cyan/30 rounded-2xl w-full max-w-md overflow-hidden my-8 flex-shrink-0">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neon-cyan/20">
              <h2 className="text-lg font-bold text-white">
                {editingId ? 'Editar Influencer' : 'Nuevo Influencer'}
              </h2>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="p-1 text-white/60 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-white/70 mb-1.5">Nombre</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre del influencer"
                  className="w-full px-4 py-2.5 bg-neon-darker border border-neon-cyan/30 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-neon-cyan"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-1.5">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@ejemplo.com"
                  className="w-full px-4 py-2.5 bg-neon-darker border border-neon-cyan/30 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-neon-cyan"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-1.5">
                  Contraseña {editingId && <span className="text-white/40">(dejar vacío para no cambiar)</span>}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="w-full px-4 py-2.5 pr-10 bg-neon-darker border border-neon-cyan/30 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-neon-cyan"
                    required={!editingId}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-1.5">Código de Cupón</label>
                <input
                  type="text"
                  value={formData.coupon_code}
                  onChange={(e) => setFormData({ ...formData, coupon_code: e.target.value.toUpperCase() })}
                  placeholder="INFLUENCER1"
                  className="w-full px-4 py-2.5 bg-neon-darker border border-neon-cyan/30 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-neon-cyan font-mono uppercase"
                  required
                />
                <p className="text-xs text-white/40 mt-1">
                  El cupón debe existir previamente en la sección de Cupones
                </p>
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-1.5">Tipo de Comisión</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, commission_type: 'percentage', commission_value: 10 })}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                      formData.commission_type === 'percentage'
                        ? 'bg-neon-cyan/20 border-neon-cyan text-neon-cyan'
                        : 'bg-neon-darker border-neon-cyan/30 text-white/60 hover:border-neon-cyan/50'
                    }`}
                  >
                    <Percent size={18} />
                    <span className="font-medium">Porcentaje</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, commission_type: 'fixed', commission_value: 50 })}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                      formData.commission_type === 'fixed'
                        ? 'bg-neon-pink/20 border-neon-pink text-neon-pink'
                        : 'bg-neon-darker border-neon-cyan/30 text-white/60 hover:border-neon-cyan/50'
                    }`}
                  >
                    <DollarSign size={18} />
                    <span className="font-medium">$ / Perfume</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-1.5">
                  {formData.commission_type === 'percentage' 
                    ? 'Comisión (% de la venta)' 
                    : 'Comisión ($ por cada perfume vendido)'}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">
                    {formData.commission_type === 'percentage' ? '%' : '$'}
                  </span>
                  <input
                    type="number"
                    min="0"
                    max={formData.commission_type === 'percentage' ? 100 : 10000}
                    step={formData.commission_type === 'percentage' ? 0.5 : 1}
                    value={formData.commission_value}
                    onChange={(e) => setFormData({ ...formData, commission_value: parseFloat(e.target.value) })}
                    className="w-full pl-10 pr-4 py-2.5 bg-neon-darker border border-neon-cyan/30 rounded-lg text-white focus:outline-none focus:border-neon-cyan"
                    required
                  />
                </div>
                <p className="text-xs text-white/40 mt-1">
                  {formData.commission_type === 'percentage' 
                    ? 'Ej: 10% = influencer gana 10% de cada venta' 
                    : 'Ej: $50 = influencer gana $50 por cada perfume vendido'}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-5 h-5 rounded border-neon-cyan/30 bg-neon-darker text-neon-cyan focus:ring-neon-cyan"
                />
                <label htmlFor="is_active" className="text-white">
                  Influencer activo
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 px-4 py-2.5 border border-neon-cyan/30 text-white rounded-lg hover:bg-white/5 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-neon-cyan text-neon-dark font-medium rounded-lg hover:bg-neon-cyan/90 transition-colors flex items-center justify-center gap-2"
                >
                  <Save size={18} />
                  {editingId ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInfluencers;
